import { useEffect, useState, useTransition } from 'react';
import type { Table } from '@tanstack/react-table';
import Checkbox from '@/components/ui/Checkbox/Checkbox';

/*
 * HAT-1212: 70 קליקי זעם על "בחר הכל" בכותרת הטבלה ב-14 יום
 * (/new/:id/transactions, וגם ב-support-requests / supporteds שחולקים
 * את אותו הרכיב).
 *
 * הסיפור: table.toggleAllRowsSelected על מאות שורות מסמן כל אחת
 * בנפרד ומריץ re-render של גוף הטבלה - בגלל וירטואליזציה של react-virtual
 * זה עדיין נכבד, ובזמן שהעדכון רץ ה-Checkbox עצמו לא מציג עדיין את
 * המצב החדש. המשתמש רואה תיבה שלא הגיבה - וקורא לזה עוד קליק.
 *
 * הפתרון: אופטימיות. ברגע הלחיצה אנחנו מעדכנים state מקומי
 * שהתיבה נצבעת לפיו, ומקיפים את העדכון של react-table ב-startTransition
 * כדי שהרינדור הכבד יוכל להתנפנף מבלי לחסום את הצביעה של התיבה.
 * ה-effect מסנכרן את המצב האופטימיסטי חזרה למקור-האמת אחרי שהעדכון
 * נבלע (בחירת שורה בודדת ידנית, למשל, משנה את getIsAllRowsSelected).
 */
interface SelectAllHeaderProps<T> {
  table: Table<T>;
  /** SPR-1212: while the board refetches, ignore clicks so the box does not look stuck. */
  disabled?: boolean;
}

const SelectAllHeader = <T,>({ table, disabled = false }: SelectAllHeaderProps<T>) => {
  const rowCount = table.getRowModel().rows.length;
  /*
   * HAT-1212 (astra): react-table's getIsAllRowsSelected() returns true on an
   * empty dataset (vacuous truth over zero rows). Without the guard, an empty
   * board rendered a checked-looking "select all" — and toggleAllRowsSelected
   * on empty rows is a no-op, so the optimistic ref used to stick forever.
   * We treat "no rows" as "nothing to select": the box shows unchecked and
   * disabled, and no state ever flips.
   */
  const hasRows = rowCount > 0;
  const actual = hasRows && table.getIsAllRowsSelected();
  const someButNotAll = hasRows && table.getIsSomeRowsSelected();

  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  const [, startTransition] = useTransition();

  /*
   * Two release paths for the optimistic marker:
   *  1. actual caught up with optimistic → drop it right away. The common case.
   *  2. actual never moved (unselectable rows, or the transition itself was a
   *     no-op) → drop it after one frame so the box does not stay stuck. The
   *     16 ms delay is short enough that a normal transition beats it and the
   *     user never sees a flicker.
   */
  useEffect(() => {
    if (optimistic === null) return;
    if (optimistic === actual) {
      setOptimistic(null);
      return;
    }
    const handle = window.setTimeout(() => setOptimistic(null), 16);
    return () => window.clearTimeout(handle);
  }, [actual, optimistic]);

  const displayed = hasRows ? (optimistic ?? actual) : false;

  /*
   * HAT-1212: כשיש שורות מסומנות אבל לא כולן, אנחנו רוצים מצב "חלקי"
   * ולא off — המשתמש רואה שיש כבר בחירה כלשהי וקליק אחד ישלים את השאר.
   * הדגל מתעלם מ-optimistic true (כי אז displayed=true ולא נחוץ קו).
   */
  const showIndeterminate = hasRows && !displayed && someButNotAll;

  const isDisabled = disabled || !hasRows;

  return (
    <Checkbox
      checked={displayed}
      indeterminate={showIndeterminate}
      disabled={isDisabled}
      onCheckedChange={(next) => {
        if (isDisabled) return;
        setOptimistic(next);
        startTransition(() => {
          table.toggleAllRowsSelected(next);
        });
      }}
    />
  );
};

export default SelectAllHeader;
